/**

// Check if debug mode is enabled (global)
const urlParams = new URLSearchParams(window.location.search);

 * File Processor Module - Обработка и фильтрация файлов
 * 
 * Phase 4: File Management Migration
 * Migrated from main.js lines 420-490 (file filtering logic)
 */


/**
 * File Processor Class - handles file processing and filtering
 */
class FileProcessor {
    constructor() {
        // Define regex patterns for confidential file filtering
        this.audioOrTapesRegex = /(AUDIO|Tapes)[\\/]/;
        this.confidentialRegex = /confid(ential)?|class *viii/i;
        this.complexConfidentialRegex = /\(confid(ential)?\)|confidential (guardian|intelligence)|(disconnections)( *[\u2013\u2014-] *)?confidential|confidencial/i;
        
        // Statistics
        this.stats = {
            totalProcessed: 0,
            totalFiltered: 0,
            confidentialRemoved: 0,
            lastProcessTime: null
        };
        
        // Initialization logging only in debug mode
    }
    
    /**
     * Filter confidential files from the list
     * @param {Array} filesList - Array of file objects
     * @returns {Array} - Filtered files array
     */
    filterConfidentialFiles(filesList) {
        if (!Array.isArray(filesList)) {
            if (debugMode === '1') {
                console.warn('[FileProcessor] Invalid files list provided');
            }
            return [];
        }
        
        const startTime = Date.now();
        const originalCount = filesList.length;
        
        if (window.HyperLog) {
            window.HyperLog.debug('[FileProcessor] Starting confidential files filtering', { 
                totalFiles: originalCount 
            });
        }
        
        const filteredFiles = filesList.filter(({ n, p }) => {
            const filePath = `${p}/${n}`;
            
            // Check if file should be filtered out
            const isConfidential = (
                (this.audioOrTapesRegex.test(filePath) && this.confidentialRegex.test(filePath)) ||
                this.complexConfidentialRegex.test(filePath)
            );
            
            return !isConfidential;
        });
        
        const filteredCount = originalCount - filteredFiles.length;
        const processTime = Date.now() - startTime;
        
        // Update statistics
        this.stats.totalProcessed += originalCount;
        this.stats.totalFiltered += filteredCount;
        this.stats.confidentialRemoved += filteredCount;
        this.stats.lastProcessTime = processTime;
        
        if (window.HyperLog) {
            window.HyperLog.debug('[FileProcessor] Confidential files filtered', { 
                originalCount: originalCount,
                filteredCount: filteredFiles.length,
                removedCount: filteredCount,
                processTime: processTime,
                sample: filteredFiles.slice(0, 5)
            });
        }
        
        return filteredFiles;
    }
    
    /**
     * Validate file object structure
     * @param {Object} file - File object to validate
     * @returns {boolean} - True if valid
     */
    validateFileObject(file) {
        if (!file || typeof file !== 'object') {
            return false;
        }
        
        // Check required properties
        const nameVal = (typeof file.n === 'string' ? file.n : (typeof file.name === 'string' ? file.name : ''));
        const pathVal = (typeof file.p === 'string' ? file.p : (typeof file.path === 'string' ? file.path : ''));
        const hasName = nameVal.length > 0;
        const hasPath = pathVal.length > 0;
        
        return hasName && hasPath;
    }
    
    /**
     * Sanitize and normalize file objects
     * @param {Array} filesList - Array of file objects
     * @returns {Array} - Sanitized files array
     */
    sanitizeFiles(filesList) {
        if (!Array.isArray(filesList)) {
            return [];
        }
        
        const startTime = Date.now();
        const originalCount = filesList.length;
        
        // Starting file sanitization logging only in debug mode
        
        const sanitizedFiles = filesList
            .filter(file => this.validateFileObject(file))
            .map(file => {
                const nameVal = (typeof file.n === 'string' ? file.n : (typeof file.name === 'string' ? file.name : '')).trim();
                const pathVal = (typeof file.p === 'string' ? file.p : (typeof file.path === 'string' ? file.path : '')).trim();
                const sizeVal = (typeof file.s === 'number' ? file.s : (typeof file.size === 'number' ? file.size : 0));
                const dateVal = (typeof file.d === 'string' ? file.d : (typeof file.date === 'string' ? file.date : ''));
                // Ensure normalized short keys exist and override any legacy fields
                return {
                    ...file,
                    n: nameVal,
                    p: pathVal,
                    s: sizeVal,
                    d: dateVal
                };
            });
        
        const processTime = Date.now() - startTime;
        const invalidCount = originalCount - sanitizedFiles.length;
        
        if (invalidCount > 0) {
            if (debugMode === '1') {
                console.warn(`[FileProcessor] Removed ${invalidCount} invalid file objects`);
            }
        }
        
        
        return sanitizedFiles;
    }
    
    /**
     * Apply custom filters to files list
     * @param {Array} filesList - Array of file objects
     * @param {Object} filters - Filter configuration
     * @returns {Array} - Filtered files array
     */
    applyCustomFilters(filesList, filters = {}) {
        if (!Array.isArray(filesList)) {
            return [];
        }
        
        const {
            extensions = null,      // Array of allowed extensions
            sizeMin = null,         // Minimum file size
            sizeMax = null,         // Maximum file size
            pathIncludes = null,    // Path must include these strings
            pathExcludes = null,    // Path must not include these strings
            namePattern = null      // Name must match this regex
        } = filters;
        
        if (window.HyperLog) {
            window.HyperLog.debug('[FileProcessor] Applying custom filters', { 
                filters: filters,
                totalFiles: filesList.length
            });
        }
        
        const filteredFiles = filesList.filter(file => {
            // Extension filter
            if (extensions && Array.isArray(extensions)) {
                const fileExt = file.n.toLowerCase().split('.').pop();
                if (!extensions.includes(fileExt)) {
                    return false;
                }
            }
            
            // Size filters
            if (sizeMin !== null && file.s < sizeMin) {
                return false;
            }
            if (sizeMax !== null && file.s > sizeMax) {
                return false;
            }
            
            // Path filters
            if (pathIncludes && Array.isArray(pathIncludes)) {
                const filePath = `${file.p}/${file.n}`;
                if (!pathIncludes.some(include => filePath.includes(include))) {
                    return false;
                }
            }
            
            if (pathExcludes && Array.isArray(pathExcludes)) {
                const filePath = `${file.p}/${file.n}`;
                if (pathExcludes.some(exclude => filePath.includes(exclude))) {
                    return false;
                }
            }
            
            // Name pattern filter
            if (namePattern && namePattern instanceof RegExp) {
                if (!namePattern.test(file.n)) {
                    return false;
                }
            }
            
            return true;
        });
        
        if (window.HyperLog) {
            window.HyperLog.debug('[FileProcessor] Custom filters applied', { 
                originalCount: filesList.length,
                filteredCount: filteredFiles.length,
                removedCount: filesList.length - filteredFiles.length
            });
        }
        
        return filteredFiles;
    }
    
    /**
     * Filter files based on country restrictions
     * @param {Array} filesList - Array of file objects
     * @param {string|null} userCountry - User's country code (ISO 3166-1 alpha-2) or null
     * @returns {Array} - Filtered files array (files with hide mode removed, others marked with restriction info)
     */
    filterCountryRestrictions(filesList, userCountry = null) {
        if (!Array.isArray(filesList)) {
            return [];
        }
        
        if (!userCountry) {
            // If no country, still mark restricted files but don't filter
            return filesList.map(file => {
                if (file.r) {
                    file._restrictionMode = this._getRestrictionMode(file.r, null);
                }
                return file;
            });
        }
        
        const userCountryUpper = userCountry.toUpperCase();
        let hiddenCount = 0;
        
        const filteredFiles = filesList.filter(file => {
            if (!file.r) {
                return true; // No restrictions
            }
            
            const mode = this._getRestrictionMode(file.r, userCountryUpper);
            file._restrictionMode = mode; // Store mode for UI display
            
            // Hide files with 'hide' mode
            if (mode === 'hide') {
                hiddenCount++;
                return false;
            }
            
            return true; // Keep files with block/warn/allow
        });
        
        if (window.HyperLog && hiddenCount > 0) {
            window.HyperLog.debug('[FileProcessor] Country restrictions filtered', {
                userCountry: userCountryUpper,
                hiddenCount: hiddenCount,
                remainingCount: filteredFiles.length
            });
        }
        
        return filteredFiles;
    }
    
    /**
     * Get restriction mode for a file based on restriction string and user country
     * @param {string} restrictionString - Restriction string like "RU:h|MD:b|OTH:w"
     * @param {string|null} userCountry - User's country code (uppercase) or null
     * @returns {string} - Mode: 'hide', 'block', 'warn', or 'allow'
     */
    _getRestrictionMode(restrictionString, userCountry) {
        if (!restrictionString) {
            return 'allow';
        }
        
        // Parse restriction string: "RU:h|MD:b|OTH:w"
        const restrictions = {};
        for (const part of restrictionString.split('|')) {
            if (!part.includes(':')) continue;
            const [countryCode, modeLetter] = part.split(':', 2);
            const modeMap = { 'h': 'hide', 'b': 'block', 'w': 'warn', 'a': 'allow' };
            restrictions[countryCode.toUpperCase()] = modeMap[modeLetter.toLowerCase()] || 'allow';
        }
        
        // Determine applicable mode
        if (userCountry && restrictions[userCountry]) {
            return restrictions[userCountry];
        } else if (restrictions['OTH']) {
            return restrictions['OTH'];
        }
        
        return 'allow';
    }
    
    /**
     * Process complete files list with all filters
     * @param {Array} filesList - Raw files list
     * @param {Object} options - Processing options
     * @returns {Array} - Fully processed files list
     */
    processFilesList(filesList, options = {}) {
        const {
            sanitize = true,
            filterConfidential = true,
            customFilters = null,
            sortBy = null,
            filterCountryRestrictions = false,
            userCountry = null
        } = options;
        
        if (!Array.isArray(filesList)) {
            if (debugMode === '1') {
                console.error('[FileProcessor] Invalid files list provided');
            }
            return [];
        }
        
        const startTime = Date.now();
        
        // Starting complete file processing logging only in debug mode
        
        let processedFiles = [...filesList];
        
        // Step 1: Sanitize file objects
        if (sanitize) {
            processedFiles = this.sanitizeFiles(processedFiles);
        }
        
        // Step 2: Filter confidential files
        if (filterConfidential) {
            processedFiles = this.filterConfidentialFiles(processedFiles);
        }
        
        // Step 3: Filter country restrictions
        if (filterCountryRestrictions) {
            processedFiles = this.filterCountryRestrictions(processedFiles, userCountry);
        }
        
        // Step 4: Apply custom filters
        if (customFilters) {
            processedFiles = this.applyCustomFilters(processedFiles, customFilters);
        }
        
        // Step 5: Sort if requested
        if (sortBy) {
            processedFiles = this.sortFiles(processedFiles, sortBy);
        }
        
        const processTime = Date.now() - startTime;
        
        return processedFiles;
    }
    
    /**
     * Sort files array by specified criteria
     * @param {Array} filesList - Files to sort
     * @param {string|Object} sortBy - Sort criteria
     * @returns {Array} - Sorted files array
     */
    sortFiles(filesList, sortBy) {
        if (!Array.isArray(filesList)) {
            return [];
        }
        
        const sortConfig = typeof sortBy === 'string' ? { field: sortBy } : sortBy;
        const { field = 'n', order = 'asc' } = sortConfig;
        
        const sortedFiles = [...filesList].sort((a, b) => {
            let valueA = a[field];
            let valueB = b[field];
            
            // Handle string comparison
            if (typeof valueA === 'string' && typeof valueB === 'string') {
                valueA = valueA.toLowerCase();
                valueB = valueB.toLowerCase();
            }
            
            if (valueA < valueB) {
                return order === 'asc' ? -1 : 1;
            }
            if (valueA > valueB) {
                return order === 'asc' ? 1 : -1;
            }
            return 0;
        });
        
        if (window.HyperLog) {
            window.HyperLog.debug('[FileProcessor] Files sorted', { 
                field: field,
                order: order,
                count: sortedFiles.length
            });
        }
        
        return sortedFiles;
    }
    
    /**
     * Get processing statistics
     * @returns {Object} - Statistics object
     */
    getStats() {
        return { ...this.stats };
    }
    
    /**
     * Reset processing statistics
     */
    resetStats() {
        this.stats = {
            totalProcessed: 0,
            totalFiltered: 0,
            confidentialRemoved: 0,
            lastProcessTime: null
        };
        
        if (window.HyperLog) {
            window.HyperLog.debug('[FileProcessor] Statistics reset');
        }
    }
}

// Create singleton instance
const fileProcessor = new FileProcessor();

// Export for use in other modules
export { fileProcessor };

// Global export for backward compatibility
window.fileProcessor = fileProcessor;

// Register with migration controller
//         'filterConfidentialFiles',
//         'sanitizeFiles',
//         'applyCustomFilters',
//         'processFilesList',
//         'sortFiles',
//         'validateFileObject',
//         'getStats',
//         'resetStats'
    
//     // Mark as migrated from main.js
// }

// Module registration logging only in debug mode 